import { TFormattedMoney } from "@sembark-travel/money"
import { IUser } from "../../Auth"
import { TCabPriceCalculationMetric } from "../../CabPriceCalculationMetrics"
import { ICabType } from "../../CabTypes"
import { TSheetFile, TSheetFileVersion } from "../store"
import { XHRInstance } from "@sembark-travel/xhr"
import { formatDate } from "@sembark-travel/datetime-utils"
import config from "../../config"
import {
	IAddCabMetricPrices,
	ICabPriceCalculationMetrics,
	ICabPriceSeasons,
	ISeasonDateRange,
} from "./NewItem"

export type TCabMetricSheetFileCreatePayload = {
	folder: number // ID of the Folder
	file_name: string // file's name (existing or new one)
	file_id?: number // if file already exists
	file_description?: string // optional file description
	currency: string
	trip_destinations?: Array<number> // IDs of selected destinations
	timezone_offset: number // timezone offset of UTC, use the config.timezoneOffset
	seasons: Array<{
		name: string
		currency?: string
		intervals: Array<{
			start_date: string // date formatted as "YYYY-MM-DD"
			end_date: string // date formatted as "YYYY-MM-DD"
			days_of_week?: Array<
				"Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun"
			>
		}>
	}>
	data: Array<{
		cab_type: string
		metrics: Array<{
			metric: number
			prices: Array<{ amount: number } | null> // The indexed of these must match with the season's indexes. Don't send the season ids.
		}>
	}>
}
export type TCabMetricSheetFileInterval = {
	start_date: string
	end_date: string
	days_of_week?: Array<"Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun">
}

export type TCabMetricSheetFileSeason = {
	id: number
	name: string
	currency: string
	for_bookings: boolean
	intervals: Array<TCabMetricSheetFileInterval>
	timezone_offset: number
}

export type TCabMetricSheetFileData = {
	id: number
	created_at: string
	created_by?: IUser
	currency: string
	seasons: Array<TCabMetricSheetFileSeason>
	file_version: TSheetFileVersion<TCabMetricSheetFileData>
	prices: Array<TCabMetricSheetFilePrice>
	history?: Array<TCabMetricSheetFileData> // only available when loading full details of this file data. This is the list of all the saves for a single version
}

export type TCabMetricSheetFilePrice = {
	id: number
	cab_type: ICabType // already defined in Cab Types
	metrics: Array<{
		id: number
		metric: TCabPriceCalculationMetric // already defined in Metrics
		prices: null | {
			[season_id: string]: {
				// linked with season id
				season_id: number
				amount: TFormattedMoney // already defined in money utils
			}
		}
	}>
}

export function cabPriceCalculationMetricPricesSheetsXHR(xhr: XHRInstance) {
	return {
		async saveCabMetricSheetFile(
			sheetData: IAddCabMetricPrices,
			folderId: number,
			fileName: string,
			fileId?: number
		): Promise<TSheetFileVersion<TCabMetricSheetFileData>> {
			const createFilePayloadData: TCabMetricSheetFileCreatePayload = {
				folder: folderId,
				file_name: fileName,
				file_id: fileId,
				currency: sheetData.config.currency,
				trip_destinations: [],
				timezone_offset: config.timezoneOffset,
				seasons:
					sheetData.seasons?.map((season: ICabPriceSeasons) => ({
						name: season.name,
						currency: sheetData.config.currency, // TODO-LATER - Currently Assuming same currency for all seasons
						intervals: season.date_ranges.map(
							(dateRange: ISeasonDateRange) => ({
								start_date: dateRange.start_date
									? formatDate(dateRange.start_date, "YYYY-MM-DD")
									: "",
								end_date: dateRange.end_date
									? formatDate(dateRange.end_date, "YYYY-MM-DD")
									: "",
								// days_of_week: [], // TODO - Add days of the week if needed
							})
						),
					})) || [],
				data: sheetData.cab_prices.map((cabPrice) => ({
					cab_type: cabPrice.cab_type?.name || "",
					metrics: (cabPrice.cab_price_calculation_metrics || [])
						.filter((metric: ICabPriceCalculationMetrics) => metric?.metric?.id)
						.map((metric: ICabPriceCalculationMetrics) => {
							return {
								metric: metric.metric?.id || 0,
								prices: (
									sheetData.seasons?.map((season: ICabPriceSeasons) => ({
										amount: metric?.season_prices?.[season.id]?.price ?? 0,
									})) ?? []
								).map((price) => (price.amount === 0 ? null : price)),
							}
						}),
				})),
			}

			return xhr
				.post(
					"/data-drive/cab-price-calculation-metric-prices/files",
					createFilePayloadData
				)
				.then((resp) => resp.data)
		},
		async uploadCabMetricSheetFile(
			metricVersionId: string
		): Promise<TSheetFileVersion<TCabMetricSheetFileData>> {
			return xhr
				.post(`/data-drive/file-versions/${metricVersionId}/upload`)
				.then((resp) => resp.data.data)
		},
		async getCabMetricSheetFileDetails(
			metricSheetId: string
		): Promise<TSheetFile<TCabMetricSheetFileData>> {
			return xhr
				.get(`/data-drive/files/${metricSheetId}`)
				.then((resp) => resp.data.data)
		},
		async getCabMetricSheetFileVersionDetails(
			metricVersionId: string
		): Promise<TSheetFileVersion<TCabMetricSheetFileData>> {
			return xhr
				.get(`/data-drive/file-versions/${metricVersionId}`)
				.then((resp) => resp.data)
		},
	}
}
